<script setup>
import { onMounted, onBeforeUnmount, ref, computed, nextTick } from "vue";
import { useGroupStore } from "@/stores/useGroupStore";
import { useUserStore } from "@/stores/useUserStore";
import * as groupService from "@/services/groupService";
import CustomTextarea from "../Generics/CustomTextarea.vue";
import { toTitleCase } from "@/utils/stringUtils";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  groupId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["close", "refresh"]);

const groupStore = useGroupStore();

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const obj = ref({
  groupId: 0,
  groupName: "",
  description: "",
});

const nameInput = ref(null);
const isSubmitting = ref(false);

// Candidatos a grupo pai: os do círculo, menos o próprio grupo e menos seus
// descendentes — escolher um descendente fecharia um ciclo (o backend também
// recusa, mas não faz sentido oferecer).
// Lista carregada à parte (GET /groups, já recortado pelo círculo no backend):
// `groupStore.groups` guarda só a PÁGINA aberta na tela de grupos, então usá-lo
// deixava de fora os grupos das outras páginas.
const allGroups = ref([]);

async function loadParentGroupOptions() {
  try {
    const response = await groupService.fetchGroups();
    allGroups.value = response.data ?? [];
  } catch (error) {
    console.warn(
      "[EditGroupForm] Falha ao carregar os grupos para o seletor de pai:",
      error.response?.data?.message || error.message
    );
    allGroups.value = [];
  }
}


// Candidato a pai: grupo do círculo, sem papel de sistema. Fora:
//  - o "Todos" do círculo (isCircleDefault): já alcança todo mundo, ser pai
//    não acrescenta nada. "Alunos" e "Professores" (também do sistema) FICAM:
//    "Alunos" é o pai natural de uma turma criada à mão;
//  - os grupos da plataforma, criados pelo Sys (o "Todos" global do acervo):
//    GET /groups os devolve a todos, mas o backend recusa pai fora do círculo
//    ("O grupo pai precisa ser do seu círculo"), então a opção só daria erro.
//    Para o próprio Sys eles são do círculo dele e continuam na lista.
function isParentCandidate(g) {
  if (g.isCircleDefault ?? g.IsCircleDefault) return false;
  const creator = String(g.createdByUserName ?? g.CreatedByUserName ?? "").trim().toLowerCase();
  const logged = String(useUserStore().currentUser?.userName ?? "").trim().toLowerCase();
  if (creator === "sys" && logged !== "sys") return false;
  return true;
}
const parentGroupOptions = computed(() => {
  const all = allGroups.value
    .filter(isParentCandidate)
    .map((g) => ({
    id: g.groupId ?? g.GroupId,
    name: g.groupName ?? g.GroupName,
    parentId: g.parentGroupId ?? g.ParentGroupId ?? null,
  }));

  const currentId = obj.value.groupId;
  const blocked = new Set([currentId]);

  // Varre até estabilizar: filho de bloqueado também é bloqueado.
  let changed = true;
  while (changed) {
    changed = false;
    for (const group of all) {
      if (!blocked.has(group.id) && group.parentId != null && blocked.has(group.parentId)) {
        blocked.add(group.id);
        changed = true;
      }
    }
  }

  return all
    .filter((g) => g.id != null && !blocked.has(g.id))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
});

// ====================================================================
// ✅ TOAST PADRÃO ADDUSERGROUPFORM.VUE (3000ms) 🚀
// (Copiado de AddActivityForm.vue para consistência)
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

// Ao salvar com sucesso, esconde só o painel do modal; o toast (num Teleport
// próprio) segue visível sozinho até o emit("close") desmontar o componente.
const hideBackdropForSuccess = ref(false);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null; // Limpa a referência
  }, 3000); // 3 segundos, padrão AddUserGroupForm
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message, onFinish) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(); // Inicia o timer de 3s para o toast

  if (typeof onFinish === "function") {
    // Esconde o painel do modal já — o toast (num Teleport próprio) segue
    // visível sozinho até o onFinish (emit close) desmontar o componente.
    hideBackdropForSuccess.value = true;
  }

  // Fecha o modal depois de 2.4s (padrão AddActivityForm.vue)
  setTimeout(() => {
    if (typeof onFinish === "function") onFinish();
  }, 2400); // Alterado de 1500ms local para 2400ms para consistência.
}
// ====================================================================

async function edit() {
  if (isSubmitting.value) return;

  // Simple validation check
  if (!obj.value.groupName || obj.value.groupName.trim().length < 3) {
    showErrorToast("O nome do grupo deve ter pelo menos 3 caracteres.");
    return;
  }

  try {
    isSubmitting.value = true;

    await groupStore.updateGroup(obj.value);

    // ✅ Toast + fecha modal depois
    showSuccessToast("Grupo atualizado com sucesso.", () => {
      emit("refresh");
      emit("close");
      isSubmitting.value = false;
    });
  } catch (error) {
    let userFriendlyMessage =
      "Ocorreu um erro desconhecido ao salvar as alterações.";
    const errorData = error?.response?.data || error;

    if (errorData && errorData.message) {
      userFriendlyMessage = errorData.message;
    } else if (error.message && typeof error.message === "string") {
      userFriendlyMessage = error.message;
    }

    console.error("Erro detalhado da API ao salvar:", errorData);

    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

function closeModal() {
  if (!isSubmitting.value) {
    emit("close");
  }
}

function handleEscKey(event) {
  if (event.key === "Escape") {
    closeModal();
  }
}

onMounted(async () => {
  try {
    const groupData = await groupStore.getGroupById(props.groupId);
    obj.value = { ...groupData };
  } catch (err) {
    console.error("Erro ao carregar grupo:", err.message);
    showErrorToast("Não foi possível carregar os dados do grupo.");
  }

  loadParentGroupOptions();

  nextTick(() => {
    if (nameInput.value) nameInput.value.focus();
  });

  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div
    v-show="!hideBackdropForSuccess"
    class="modal-backdrop"
    @click.self="!isSubmitting"
  >
    <div ref="panelRef" class="modal-panel small" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Editar Grupo</h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="edit" novalidate>
        <div class="modal-body">
          <div class="form-group">
            <label for="name" class="form-label">Nome</label>
            <input
              ref="nameInput"
              type="text"
              name="name"
              id="name"
              v-model="obj.groupName"
              class="input-base"
              placeholder="Digite o nome do grupo"
              required
              @focus="$event.target.select()"
              @blur="obj.groupName = toTitleCase(obj.groupName)"
            />
          </div>

          <div class="form-group">
            <label for="parentGroupId" class="form-label">
              Grupo pai (opcional)
            </label>
            <select
              id="parentGroupId"
              v-model="obj.parentGroupId"
              class="input-base"
            >
              <option :value="null">Nenhum - grupo de primeiro nível</option>
              <option
                v-for="option in parentGroupOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
            <span class="field-hint">
              Quem estiver neste grupo também recebe o que for concedido ao grupo
              pai.
            </span>
          </div>

          <div class="form-group">
            <label for="description" class="form-label">Descrição (opcional)</label>
            <CustomTextarea
              v-model:body="obj.description"
              placeholder="Digite uma breve descrição (opcional)"
              :is-disabled="false"
              style="min-height: 120px"
            />
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>

            <svg
              v-else
              class="mr-1 -ml-1 w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
              />
            </svg>

            <span>
              {{ isSubmitting ? "Salvando..." : "Salvar Alterações" }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
/* ✅ input-base (padrão AddAuthorForm) */
.field-hint {
  display: block;
  color: var(--text-light);
  font-size: 0.8rem;
  margin-top: 0.2rem;
}

select.input-base {
  min-height: 2.6rem;
}

</style>
